import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AcademicCapIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  CloudIcon,
  EyeIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { fetchCustomerCommandCenter } from "../../services/axios/AuthApis/CommandCenterApis";

const modules = [
  {
    key: "asset_management",
    name: "Asset Management",
    path: "/integrations",
    icon: CircleStackIcon,
    stats: [
      ["total_assets", "Total assets"],
      ["active_assets", "Active assets"],
      ["exposed_assets", "Exposed assets"],
      ["critical_assets", "Critical assets"],
    ],
  },
  {
    key: "cspm",
    name: "CSPM",
    path: "/ai-cspm",
    icon: CloudIcon,
    stats: [
      ["total_assets", "Cloud assets"],
      ["total_scans", "Scans"],
      ["total_findings", "Findings"],
      ["critical_findings", "Critical"],
      ["high_findings", "High"],
      ["average_security_score", "Security score"],
    ],
  },
  {
    key: "dspm",
    name: "DSPM",
    path: "/ai-dspm",
    icon: EyeIcon,
    stats: [
      ["total_stores_scanned", "Data stores"],
      ["stores_with_sensitive_data", "Sensitive"],
      ["stores_at_risk", "At risk"],
      ["exposed_stores", "Exposed"],
      ["critical_findings", "Critical"],
    ],
  },
  {
    key: "soc",
    name: "SOC",
    path: "/SOC",
    icon: ShieldExclamationIcon,
    stats: [
      ["total_alerts", "Alerts"],
      ["critical_alerts", "Critical"],
      ["high_alerts", "High"],
      ["open_incidents", "Open incidents"],
      ["events_count", "Events"],
    ],
  },
  {
    key: "vapt",
    name: "VAPT",
    path: "/ai-vapt",
    icon: ShieldCheckIcon,
    stats: [
      ["open_findings", "Open findings"],
      ["critical_findings", "Critical"],
      ["high_findings", "High"],
      ["total_projects", "Projects"],
    ],
  },
  {
    key: "risk_management",
    name: "Risk Management",
    path: "/risk-management",
    icon: ChartBarIcon,
    stats: [
      ["total_risks", "Risks"],
      ["critical_high_risks", "Critical / high"],
      ["moderate_risks", "Moderate"],
      ["low_risks", "Low"],
    ],
  },
  {
    key: "compliance",
    name: "Compliance",
    path: "/compliance",
    icon: ClipboardDocumentCheckIcon,
    stats: [
      ["total_controls", "Controls"],
      ["implemented_controls", "Implemented"],
      ["not_implemented_controls", "Gaps"],
      ["percent_implemented", "Implemented %"],
    ],
  },
  {
    key: "tprm",
    name: "TPRM",
    path: "/ai-tprm",
    icon: BuildingOfficeIcon,
    stats: [
      ["total_vendors", "Vendors"],
      ["active_vendors", "Active"],
      ["high_risk_vendors", "High risk"],
    ],
  },
  {
    key: "lms",
    name: "LMS",
    path: "/ai-lms",
    icon: AcademicCapIcon,
    stats: [
      ["total_courses", "Courses"],
      ["completed_courses", "Completed"],
    ],
  },
];

const severities = [
  { key: "critical", label: "Critical", border: "border-rose-200", accent: "text-rose-700", dot: "bg-rose-600" },
  { key: "high", label: "High", border: "border-orange-200", accent: "text-orange-700", dot: "bg-orange-500" },
  { key: "medium", label: "Medium", border: "border-amber-200", accent: "text-amber-700", dot: "bg-amber-500" },
  { key: "low", label: "Low", border: "border-emerald-200", accent: "text-emerald-700", dot: "bg-emerald-600" },
];

const severityRank = Object.fromEntries(severities.map(({ key }, index) => [key, index]));
const REFRESH_INTERVAL_MS = 60_000;

const relativeTime = (timestamp) => {
  if (!timestamp) return "Time not available";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "Time not available";
  const differenceSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const units = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const match = units.find(([, seconds]) => Math.abs(differenceSeconds) >= seconds);
  if (!match) return formatter.format(differenceSeconds, "second");
  return formatter.format(Math.round(differenceSeconds / match[1]), match[0]);
};

const formatMetricValue = (value, metricKey) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    const formatted = value.toLocaleString();
    return metricKey === "percent_implemented" ? `${formatted}%` : formatted;
  }
  if (typeof value === "string" && value.trim()) return value.trim();
  return "—";
};

const ModuleCard = ({ definition, result, onNavigate }) => {
  const Icon = definition.icon;
  const stats = definition.stats;
  const securityStatus = result?.status && !["unknown", "available"].includes(result.status)
    ? result.status
    : null;
  const hasFreshTimestamp = result?.last_updated && result?.freshness_status === "fresh";

  return (
    <button
      type="button"
      onClick={() => onNavigate(definition.path)}
      aria-label={`Open ${definition.name} dashboard`}
      className="group flex h-full w-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition duration-150 hover:border-primary-300 hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    >
      <span className="flex min-w-0 items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-3">
          <span className="flex shrink-0 items-center justify-center rounded-lg bg-primary-50 p-2 text-primary-600">
            <Icon aria-hidden="true" className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold leading-5 text-gray-900">{definition.name}</span>
          </span>
        </span>
        <span aria-hidden="true" className="shrink-0 text-base leading-none text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-primary-600">→</span>
      </span>
      <span className="mt-3 grid grid-cols-2 content-start gap-x-3 gap-y-2 border-t border-gray-100 pt-2.5">
        {stats.map(([key, label]) => (
          <span key={key} className="flex min-w-0 flex-col items-start">
            <span className="block w-full truncate text-xs leading-4 text-gray-500">{label}</span>
            <span className="mt-0.5 block w-full truncate text-base font-semibold leading-5 tabular-nums text-gray-900">
              {formatMetricValue(result?.stats?.[key], key)}
            </span>
          </span>
        ))}
      </span>
      {(securityStatus || hasFreshTimestamp) && (
        <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 pt-2 text-[11px] leading-4 text-gray-500">
        {securityStatus && (
          <span className="capitalize">
            Security: <span className="font-medium text-gray-700">{securityStatus}</span>
          </span>
        )}
        {hasFreshTimestamp && (
          <span title={new Date(result.last_updated).toLocaleString()}>
            Updated {relativeTime(result.last_updated)}
          </span>
        )}
        </span>
      )}
    </button>
  );
};

const ExecutiveMetrics = ({ metrics, onNavigate }) => {
  if (!Array.isArray(metrics) || !metrics.length) return null;

  return (
    <section aria-labelledby="executive-overview-title">
      <div className="mb-2 flex items-end justify-between gap-3">
        <div>
          <h2 id="executive-overview-title" className="text-base font-semibold leading-5 text-gray-900">Executive Overview</h2>
          <p className="mt-0.5 text-xs leading-4 text-gray-500">Key security and operations metrics across your modules.</p>
        </div>
      </div>
      <div className="grid auto-rows-fr grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {metrics.map((metric) => {
          const target = modules.find(({ key }) => key === metric.module);
          if (!target) return null;
          const Icon = target.icon;
          return (
            <button
              key={metric.key}
              type="button"
              onClick={() => onNavigate(target.path)}
              aria-label={`Open ${target.name} dashboard for ${metric.label}`}
              className="group flex min-h-[108px] min-w-0 items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition-colors hover:border-primary-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="block truncate text-xs font-medium leading-4 text-gray-500">{metric.label}</span>
                <span className="mt-1 block text-2xl font-bold leading-7 tabular-nums text-gray-900">
                  {formatMetricValue(metric.value, metric.key)}
                </span>
                <span className="mt-1 block truncate text-[11px] leading-4 text-gray-500">{target.name}</span>
              </span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                <Icon aria-hidden="true" className="h-4 w-4" />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const RecentSecurityActivity = ({ activity, onNavigate }) => {
  const items = (Array.isArray(activity) ? activity : []).filter((item) => (
    modules.some((module) => module.key === item.module)
    && typeof item.title === "string"
  ));

  return (
    <section aria-labelledby="recent-security-activity-title" className="min-w-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="mb-2">
        <h2 id="recent-security-activity-title" className="text-base font-semibold leading-5 text-gray-900">Recent Security Activity</h2>
        <p className="mt-0.5 text-xs leading-4 text-gray-500">Latest persisted scan, finding, and control updates.</p>
      </div>
      {items.length ? (
        <div className="divide-y divide-gray-100">
          {items.map((item) => {
            const target = modules.find(({ key }) => key === item.module);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(target.path)}
                aria-label={`Open ${target.name}: ${item.title}`}
                className="flex w-full min-w-0 items-center justify-between gap-3 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
              >
                <span className="flex min-w-0 items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-600" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium leading-5 text-gray-900">{item.title}</span>
                    {item.description && <span className="mt-0.5 block truncate text-xs text-gray-500">{item.description}</span>}
                    <span className="mt-0.5 block text-[11px] leading-4 text-gray-500">{item.module_name || target.name}</span>
                  </span>
                </span>
                <time className="shrink-0 text-[11px] text-gray-500" dateTime={item.timestamp}>
                  {relativeTime(item.timestamp)}
                </time>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md bg-gray-50 px-3 py-4 text-sm text-gray-600">
          No recent security activity.
        </div>
      )}
    </section>
  );
};

const CustomerAdminCommandCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const hasSnapshot = useRef(false);
  const inFlightRef = useRef(false);

  const loadSnapshot = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const isInitialLoad = !hasSnapshot.current;
    setLoading(isInitialLoad);
    setRefreshing(!isInitialLoad);
    setError("");
    try {
      const data = await fetchCustomerCommandCenter();
      if (!data || !Array.isArray(data.modules) || !Array.isArray(data.alerts)) {
        throw new Error("The Command Center returned an invalid response.");
      }
      setSnapshot(data);
      hasSnapshot.current = true;
    } catch (loadError) {
      setError(loadError?.response?.data?.error || loadError.message || "The Command Center could not be loaded.");
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSnapshot();
    const timer = window.setInterval(() => {
      if (hasSnapshot.current) loadSnapshot();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadSnapshot]);

  const moduleResults = useMemo(
    () => new Map((snapshot?.modules || []).map((module) => [module.key, module])),
    [snapshot],
  );
  const alerts = useMemo(() => {
    const availableAlerts = (snapshot?.alerts || []).filter((alert) => (
      Object.prototype.hasOwnProperty.call(severityRank, alert.severity)
      && modules.some((module) => module.key === alert.module)
    ));
    return severityFilter
      ? availableAlerts.filter((alert) => alert.severity === severityFilter)
      : availableAlerts;
  }, [snapshot, severityFilter]);
  const attentionItems = useMemo(() => (
    (snapshot?.requires_attention || []).filter((alert) => (
      ["critical", "high"].includes(alert.severity)
      && modules.some((module) => module.key === alert.module)
    ))
  ), [snapshot]);
  const navigateToModule = useCallback((path) => navigate(path), [navigate]);

  if (loading) {
    return (
      <main className="w-full space-y-4 px-4 py-4 sm:px-5 lg:px-6 2xl:px-8" aria-label="Loading Command Center">
        <div className="h-28 animate-pulse rounded-lg bg-gray-200" />
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {severities.map(({ key }) => <div key={key} className="h-24 animate-pulse rounded-xl bg-gray-200" />)}
        </div>
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
          {modules.map(({ key }) => <div key={key} className="h-40 animate-pulse rounded-xl bg-gray-200" />)}
        </div>
      </main>
    );
  }

  if (!snapshot) {
    return (
      <main className="w-full px-4 py-6 sm:px-5 lg:px-6" aria-labelledby="command-center-title">
        <div className="flex flex-col gap-4 rounded-lg border border-rose-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 id="command-center-title" className="text-lg font-semibold text-gray-900">Command Center unavailable</h1>
            <p role="alert" className="mt-1 text-sm leading-5 text-rose-700">{error || "Dashboard data could not be loaded."}</p>
          </div>
          <button type="button" onClick={loadSnapshot} className="shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
            Retry
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-w-0 space-y-4 px-4 py-4 sm:px-5 lg:px-6 2xl:px-8">
      <section className="flex flex-col justify-between gap-3 rounded-lg bg-slate-950 p-4 text-white shadow-sm sm:flex-row sm:items-center">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-300">Customer Security Operations</p>
          <p className="mt-0.5 text-sm font-medium text-slate-300">
            {user?.company_name || user?.customername || "Organization"}
          </p>
          <h1 id="command-center-title" className="mt-0.5 text-2xl font-bold leading-tight tracking-tight sm:text-[1.7rem]">Command Center</h1>
          <p className="mt-1.5 text-xs text-slate-400">
            {/* Read-only overview · Auto-refreshes every minute */}
            {snapshot.generated_at ? ` · Updated ${relativeTime(snapshot.generated_at)}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={loadSnapshot}
          disabled={refreshing}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-white/15 disabled:cursor-wait disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-950"
        >
          <ArrowPathIcon aria-hidden="true" className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      <ExecutiveMetrics
        metrics={snapshot.executive_metrics}
        onNavigate={navigateToModule}
      />

      <section aria-labelledby="overall-posture-title" className="min-w-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-2">
          <h2 id="overall-posture-title" className="text-base font-semibold leading-5 text-gray-900">Security Overview</h2>
          <p className="mt-0.5 text-xs leading-4 text-gray-500">Current security alerts by severity.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {severities.map(({ key, label, border, accent }) => (
              <button
                key={key}
                type="button"
                aria-pressed={severityFilter === key}
                onClick={() => {
                  setSeverityFilter((current) => current === key ? "" : key);
                  document.getElementById("command-center-alerts")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`flex min-w-0 items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${border} ${severityFilter === key ? "ring-2 ring-primary-500 ring-offset-1" : ""}`}
              >
                <span className="min-w-0">
                  <span className={`block truncate text-[11px] font-semibold uppercase tracking-wide ${accent}`}>{label}</span>
                  <span className="mt-0.5 block truncate text-[11px] leading-4 text-gray-500">{severityFilter === key ? "Filtered" : "View alerts"}</span>
                </span>
                <span className="shrink-0 text-xl font-bold leading-6 tabular-nums text-gray-900">{(snapshot.overview?.[key] ?? 0).toLocaleString()}</span>
              </button>
            ))}
        </div>
      </section>

      <section aria-labelledby="requires-attention-title" className="min-w-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-2">
          <h2 id="requires-attention-title" className="text-base font-semibold leading-5 text-gray-900">Requires Attention</h2>
          <p className="mt-0.5 text-xs leading-4 text-gray-500">Critical and high-severity security findings and alerts.</p>
        </div>
        {attentionItems.length ? (
            <div className="grid items-start gap-x-3 md:grid-cols-2">
              {attentionItems.map((alert) => {
                const target = modules.find(({ key }) => key === alert.module);
                const severity = severities.find(({ key }) => key === alert.severity);
                return (
                  <button
                    key={alert.id}
                    type="button"
                    onClick={() => navigateToModule(target.path)}
                    aria-label={`Open ${target.name}: ${alert.title}`}
                    className="group flex min-w-0 items-start gap-2.5 border-b border-gray-100 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                  >
                    <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${severity.dot}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[10px] font-semibold uppercase leading-4 tracking-wide ${severity.accent}`}>{severity.label}</span>
                      <span className="mt-0.5 block break-words text-sm font-medium leading-5 text-gray-900">{alert.title}</span>
                      {alert.description && <span className="mt-0.5 block line-clamp-1 text-xs leading-4 text-gray-500">{alert.description}</span>}
                      <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] leading-4 text-gray-500">
                        <span>{alert.module_name || target.name}</span>
                        {alert.timestamp && <time dateTime={alert.timestamp}>{relativeTime(alert.timestamp)}</time>}
                      </span>
                    </span>
                    <span aria-hidden="true" className="mt-1 shrink-0 text-xs text-gray-400 transition group-hover:text-primary-600">→</span>
                  </button>
                );
              })}
            </div>
        ) : (
          <div className="rounded-md bg-gray-50 px-3 py-4 text-sm text-gray-600">
            No high-priority items are available.
          </div>
        )}
      </section>

      <div className="min-w-0 space-y-3">
      <section id="command-center-alerts" aria-labelledby="command-center-alerts-title" className="min-w-0 scroll-mt-4 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 id="command-center-alerts-title" className="text-base font-semibold leading-5 text-gray-900">Security Alerts</h2>
            <p className="mt-0.5 text-xs leading-4 text-gray-500">Prioritized alerts requiring attention.</p>
          </div>
          {severityFilter && (
            <button type="button" onClick={() => setSeverityFilter("")} className="text-xs font-medium text-primary-700 hover:text-primary-800 focus:outline-none focus:underline">
              Clear {severityFilter} filter
            </button>
          )}
        </div>
        {alerts.length ? (
          <div className="divide-y divide-gray-100">
            {alerts.map((alert) => {
              const severity = severities.find(({ key }) => key === alert.severity);
              const target = modules.find(({ key }) => key === alert.module);
              return (
                <button
                  key={alert.id}
                  type="button"
                  onClick={() => navigateToModule(target.path)}
                  aria-label={`Open ${target.name}: ${alert.title}`}
                  className="group flex w-full min-w-0 items-start gap-2.5 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 sm:items-center sm:gap-3"
                >
                  <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${severity.dot}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide ${severity.accent}`}>{severity.label}</span>
                      <span className="break-words text-sm font-medium leading-5 text-gray-900">{alert.title}</span>
                    </span>
                    {alert.description && <span className="mt-0.5 block line-clamp-2 text-xs leading-4 text-gray-600">{alert.description}</span>}
                    <span className="mt-0.5 block text-[11px] leading-4 text-gray-500 sm:hidden">
                      {target.name}{alert.timestamp ? ` · ${relativeTime(alert.timestamp)}` : ""}
                    </span>
                  </span>
                  <span className="hidden shrink-0 items-center gap-2 text-[11px] text-gray-500 sm:flex">
                    <span className="max-w-28 truncate">{target.name}</span>
                    {alert.timestamp && <time className="whitespace-nowrap" dateTime={alert.timestamp}>{relativeTime(alert.timestamp)}</time>}
                  </span>
                  <span aria-hidden="true" className="mt-1 shrink-0 text-xs font-semibold text-gray-400 transition group-hover:text-primary-600">→</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-md bg-gray-50 px-3 py-5 text-center">
            <p className="text-sm font-medium text-gray-800">{severityFilter ? `No ${severityFilter} alerts` : "No active security alerts"}</p>
            <p className="mt-1 text-xs leading-4 text-gray-500">No matching alerts were found in the available module summaries.</p>
          </div>
        )}
      </section>

      <RecentSecurityActivity
        activity={snapshot.recent_activity}
        onNavigate={navigateToModule}
      />
      </div>

      <section aria-labelledby="module-overview-title">
        <div className="mb-2">
          <h2 id="module-overview-title" className="text-base font-semibold leading-5 text-gray-900">Module Overview</h2>
          <p className="mt-0.5 text-xs leading-4 text-gray-500">Security posture across monitored security and compliance domains.</p>
        </div>
        <div className="grid min-w-0 items-stretch gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          {modules.map((definition) => (
            <ModuleCard
              key={definition.key}
              definition={definition}
              result={moduleResults.get(definition.key)}
              onNavigate={navigateToModule}
            />
          ))}
        </div>
      </section>
    </main>
  );
};

export default CustomerAdminCommandCenter;